import { Amplify } from "aws-amplify";
import {
    confirmResetPassword,
    confirmSignIn,
    fetchAuthSession,
    forgetDevice,
    getCurrentUser,
    rememberDevice,
    resetPassword,
    type SignInOutput,
    setUpTOTP,
    signIn,
    signOut,
} from "aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { AUTH_CONFIGS } from "./authConfigs";
import { setCognitoSession } from "./localStorage";
import { CognitoSession, MfaType, SelectMfaTypeOptions } from "./types";

// Only the conventional (username/password) login uses the Amplify SDK.
// Federated logins (google, vy) use the oauth flow in auth.ts.

export type AuthenticateResult = {
    success?: boolean;
    mfaRequired?: boolean;
    mfaType?: MfaType;
    selectMfaTypeOptions?: SelectMfaTypeOptions;
};

// Not exported from aws-amplify/auth
type AuthMFAType = "SMS" | "TOTP" | "EMAIL";

let configured = false;

const ensureConfigured = () => {
    if (configured) {
        return;
    }
    Amplify.configure({
        Auth: {
            Cognito: {
                userPoolId:
                    AUTH_CONFIGS.aws_cognito_conventional_login.user_pool_id ||
                    "",
                userPoolClientId:
                    AUTH_CONFIGS.aws_cognito_conventional_login.client_id || "",
            },
        },
    });
    configured = true;
};

const toMfaType = (type: AuthMFAType): MfaType | undefined => {
    if (type === "SMS") {
        return "SMS_MFA";
    }
    if (type === "TOTP") {
        return "SOFTWARE_TOKEN_MFA";
    }
};

const toAuthMfaType = (type: MfaType): AuthMFAType =>
    type === "SOFTWARE_TOKEN_MFA" ? "TOTP" : "SMS";

export const toAuthenticateResult = (
    output: SignInOutput,
): AuthenticateResult => {
    const { nextStep } = output;
    switch (nextStep.signInStep) {
        case "DONE":
            return { success: true };
        case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
            return { mfaRequired: true, mfaType: "SMS_MFA" };
        case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
            return { mfaRequired: true, mfaType: "SOFTWARE_TOKEN_MFA" };
        case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION":
            return {
                mfaRequired: true,
                selectMfaTypeOptions: {
                    allowedMfaTypes: (nextStep.allowedMFATypes || [])
                        .map(toMfaType)
                        .filter((type): type is MfaType => !!type),
                },
            };
        default:
            throw new Error(`Unexpected sign in step: ${nextStep.signInStep}`);
    }
};

export const signOutLocal = async (): Promise<void> => {
    ensureConfigured();
    try {
        await signOut();
    } catch (error) {
        console.log("Error signOut", error);
    }
};

export const authenticate = async (
    username: string,
    password: string,
): Promise<AuthenticateResult> => {
    ensureConfigured();
    // Amplify refuses to sign in while it still holds tokens from an
    // earlier session, so clear any leftovers first.
    await signOutLocal();
    const output = await signIn({ username, password });
    return toAuthenticateResult(output);
};

export const getMfaTotpSecret = (
    associateSecretCode: (secretCode: string) => void,
    onFailure: (err: any) => void,
) => {
    ensureConfigured();
    setUpTOTP()
        .then((output) => associateSecretCode(output.sharedSecret))
        .catch(onFailure);
};

export const verifyMfa = async (
    mfaCode: string,
    shouldRememberDevice: boolean,
): Promise<void> => {
    ensureConfigured();
    const output = await confirmSignIn({ challengeResponse: mfaCode });
    const result = toAuthenticateResult(output);
    if (!result.success) {
        throw new Error("Unexpected result");
    }
    if (shouldRememberDevice) {
        // ignore error when setting device status as it is not critical
        // for the login to complete
        try {
            await rememberDevice();
        } catch (error) {
            console.log("Error rememberDevice", error);
        }
    }
};

export const selectMfaType = async (
    answer: MfaType,
): Promise<AuthenticateResult> => {
    ensureConfigured();
    const output = await confirmSignIn({
        challengeResponse: toAuthMfaType(answer),
    });
    return toAuthenticateResult(output);
};

export const forgottenPassword = async (username: string): Promise<void> => {
    ensureConfigured();
    await resetPassword({ username });
};

export const confirmForgottenPassword = async (
    username: string,
    verificationCode: string,
    newPassword: string,
): Promise<AuthenticateResult> => {
    ensureConfigured();
    await confirmResetPassword({
        username,
        confirmationCode: verificationCode,
        newPassword,
    });
    return authenticate(username, newPassword);
};

export const forgetDeviceSignOut = async (): Promise<void> => {
    ensureConfigured();
    try {
        await getCurrentUser();
    } catch {
        console.log("No Cognito User, skip");
        return;
    }
    try {
        await forgetDevice();
    } finally {
        await signOutLocal();
    }
};

const getCurrentCognitoSession = async (): Promise<
    CognitoSession | undefined
> => {
    const { tokens } = await fetchAuthSession();
    if (!tokens?.accessToken) {
        return;
    }
    const stored =
        await cognitoUserPoolsTokenProvider.authTokenStore.loadTokens();
    return {
        cognito_access_token: tokens.accessToken.toString(),
        cognito_refresh_token: stored?.refreshToken,
        cognito_config: AUTH_CONFIGS.aws_cognito_conventional_login,
    };
};

export const redirectToApp = async (): Promise<void> => {
    ensureConfigured();
    const session = await getCurrentCognitoSession();
    if (!session) {
        throw new Error("No session after sign in");
    }
    // persist session in local storage so we can load it from there in the redirect
    setCognitoSession(session);
    // reload page
    window.location.reload();
};

export const refreshCognitoIdentity = async (
    _token: string,
): Promise<CognitoSession | undefined> => {
    // Amplify keeps its own copy of the refresh token and uses that one.
    ensureConfigured();
    try {
        await fetchAuthSession({ forceRefresh: true });
        return await getCurrentCognitoSession();
    } catch (error) {
        console.error(error);
    }
};
